import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/app/ui-bits";
import { toast } from "sonner";
import {
  Package, Plus, Wrench, Warning, AirplaneTilt, ShieldCheck,
  Buildings, Gear, Pulse, FileText, ArrowsDownUp, LockKey, Sparkle,
  CheckCircle, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useNewRecordHighlight from "@/hooks/useNewRecordHighlight";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import RelationsPanel from "@/components/app/RelationsPanel";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import AssetLifecycleDrawer from "@/components/app/AssetLifecycleDrawer";
import ExportMenu from "@/components/app/ExportMenu";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";
import AssetPicker from "@/components/app/AssetPicker";

const TABS = [
  { id: "overview", label: "Overview", icon: Package },
  { id: "register", label: "Asset Register", icon: Buildings },
  { id: "categories", label: "Categories", icon: ShieldCheck },
  { id: "movements", label: "Check In / Out", icon: ArrowsDownUp },
  { id: "mobilisation", label: "Mobilisation", icon: AirplaneTilt },
  { id: "accessories", label: "Accessories", icon: Gear },
  { id: "maintenance", label: "Calibration", icon: Wrench },
  { id: "rentals", label: "Rentals", icon: FileText },
  { id: "incidents", label: "Incidents", icon: Warning },
  { id: "zone", label: "Zone Access", icon: LockKey },
  { id: "kpis", label: "KPIs", icon: Pulse },
  { id: "forms", label: "Forms Register", icon: FileText },
];

export default function Assets() {
  const [tab, setTab] = useState("overview");
  const [registerFilter, setRegisterFilter] = useState(null); // optional preset (e.g. {category:"CAT-A"} or {status:"deployed"})
  const { newId } = useNewRecordHighlight(false);
  // If arriving from AI Extractor with ?new=<id>, auto-jump to Asset Register tab
  useEffect(() => { if (newId) setTab("register"); }, [newId]);

  // Drill-through from Overview/Categories tiles → Register with preset filter
  const jumpToRegister = (preset) => {
    if (!preset) return;
    if (preset === "all") {
      setRegisterFilter(null);
    } else if (preset.startsWith("category:")) {
      setRegisterFilter({ kind: "category", value: preset.split(":")[1] });
    } else if (preset.startsWith("status:")) {
      setRegisterFilter({ kind: "status", value: preset.split(":")[1] });
    } else if (preset.startsWith("calibration:")) {
      setRegisterFilter({ kind: "calibration", value: preset.split(":")[1] });
    } else if (preset.startsWith("action:")) {
      setRegisterFilter({ kind: "action", value: preset.split(":")[1] });
    }
    // Some actions live in dedicated tabs
    const targetTab = preset.startsWith("action:open_incidents") ? "incidents"
      : preset.startsWith("action:pending_mobilisations") ? "mobilisation"
      : preset.startsWith("action:overdue_maintenance") || preset.startsWith("action:calibration_overdue") || preset.startsWith("action:calibration_due_soon") ? "maintenance"
      : preset.startsWith("action:rentals_") || preset.startsWith("action:assets_not_returned") ? "rentals"
      : preset.startsWith("action:uncategorized_assets") ? "categories"
      : "register";
    setTab(targetTab);
  };
  return (
    <div data-testid="assets-page">
      <PageHeader
        title="Asset Management"
        description="Lifecycle management of DESCON engineering & survey assets — per the DESCON QMS Asset Management Framework."
        actions={<ExportMenu module="assets" />}
      />
      <div className="p-5 md:p-8 space-y-4">
        <div className="relative rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <div className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
            {TABS.map(({ id, label, icon: Icon }) => {
              const isActive = tab === id;
              return (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-medium transition ${
                    isActive ? "bg-[#0f1e42] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  data-testid={`tab-${id}`}
                >
                  <Icon size={14} /> <span>{label}</span>
                </button>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-y-1 right-1 w-10 rounded-r-xl bg-gradient-to-l from-white to-transparent" />
        </div>

        {tab === "overview" && <Overview onJumpToRegister={jumpToRegister} />}
        {tab === "register" && <Register presetFilter={registerFilter} onClearFilter={() => setRegisterFilter(null)} />}
        {tab === "categories" && <Categories onJumpToRegister={jumpToRegister} />}
        {tab === "movements" && <Movements />}
        {tab === "mobilisation" && <Mobilisation />}
        {tab === "accessories" && <Accessories />}
        {tab === "maintenance" && <Maintenance />}
        {tab === "rentals" && <Rentals />}
        {tab === "incidents" && <Incidents />}
        {tab === "zone" && <ZoneLogs />}
        {tab === "kpis" && <KPIs />}
        {tab === "forms" && <FormsRegister />}
      </div>
    </div>
  );
}

// ── OVERVIEW ────────────────────────────────────────────────────────
function Overview({ onJumpToRegister }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/assets/summary/overview").then((r) => setS(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);
  if (err) return <div className="dc-card p-5 text-sm text-red-700">Failed to load: {err}</div>;
  if (!s) return <Loading />;

  const t = s.totals || {};
  const flags = s.accuracy_flags || [];
  const action = s.action_needed || {};
  const cal = s.calibration_summary || {};
  const breakdown = s.category_breakdown || {};
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
  const compTone = s.calibration_compliance_rate >= 90 ? "emerald" : s.calibration_compliance_rate >= 70 ? "amber" : "red";
  const compColors = { emerald: "#059669", amber: "#b8860b", red: "#dc2626" }[compTone];

  // Status totals row
  const statusTiles = [
    { l: "Total Assets",  v: t.all,        tone: "slate",   onClick: () => onJumpToRegister?.("all"),     testid: "tile-total" },
    { l: "In Store",      v: t.in_store,   tone: "slate",   onClick: () => onJumpToRegister?.("status:in_store"),   testid: "tile-instore" },
    { l: "Deployed",      v: t.deployed,   tone: "emerald", onClick: () => onJumpToRegister?.("status:deployed"),   testid: "tile-deployed" },
    { l: "Rented",        v: t.rented,     tone: "blue",    onClick: () => onJumpToRegister?.("status:rented"),     testid: "tile-rented" },
    { l: "Maintenance",   v: t.maintenance, tone: "amber",  onClick: () => onJumpToRegister?.("status:maintenance"), testid: "tile-maintenance" },
    { l: "Total Value",   v: `$${fmt(Math.round(t.total_value_usd || 0))}`, tone: "blue", testid: "tile-value" },
  ];

  // Action items (only those with count > 0)
  const actionList = [
    { k: "open_incidents", l: "Open incidents", tone: "red", tab: "incidents" },
    { k: "pending_mobilisations", l: "Pending mobilisations", tone: "amber", tab: "mobilisation" },
    { k: "calibration_overdue", l: "Calibration overdue", tone: "red", tab: "maintenance" },
    { k: "calibration_due_soon", l: "Calibration due within 30d", tone: "amber", tab: "maintenance" },
    { k: "calibration_missing", l: "Calibration data missing", tone: "amber", tab: "register" },
    { k: "overdue_maintenance", l: "Overdue maintenance", tone: "amber", tab: "maintenance" },
    { k: "rentals_expiring_30d", l: "Rentals expiring (30d)", tone: "amber", tab: "rentals" },
    { k: "assets_not_returned", l: "Assets not returned", tone: "red", tab: "rentals" },
    { k: "missing_custodian", l: "Deployed without custodian", tone: "amber", tab: "register" },
    { k: "uncategorized_assets", l: "Uncategorized assets", tone: "amber", tab: "categories" },
  ].filter((x) => (action[x.k] || 0) > 0);

  return (
    <div className="space-y-4" data-testid="assets-overview">
      {/* Status tiles */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="overview-tiles">
        {statusTiles.map((tile) => (
          <button
            key={tile.l}
            type="button"
            onClick={tile.onClick}
            disabled={!tile.onClick}
            className={`dc-card p-4 text-left transition ${tile.onClick ? "hover:border-[#1E5CBA]/50 hover:shadow-md cursor-pointer" : "cursor-default"}`}
            data-testid={tile.testid}
          >
            <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{tile.l}</div>
            <div className="mt-1 text-2xl font-display font-semibold text-slate-900">{tile.v ?? 0}</div>
          </button>
        ))}
      </section>

      {/* Accuracy flags */}
      {flags.length > 0 && (
        <section className="space-y-2" data-testid="overview-accuracy-flags">
          {flags.map((f, i) => {
            const TONE = {
              error: "border-red-200 bg-red-50/60 text-red-800",
              warning: "border-amber-200 bg-amber-50/60 text-amber-800",
              info: "border-blue-200 bg-blue-50/60 text-blue-800",
            }[f.severity] || "border-slate-200 bg-slate-50/60 text-slate-800";
            return (
              <div key={`flag-${i}`} className={`rounded-md border px-3 py-2 ${TONE} flex items-center gap-2`} data-testid={`accuracy-flag-${f.rule}`}>
                <Warning size={14} weight="fill" />
                <div className="text-sm">{f.message}</div>
              </div>
            );
          })}
        </section>
      )}

      {/* 3-column main row */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Assets by Category — clickable, with deployed/value */}
        <div className="dc-card p-5" data-testid="widget-by-category">
          <h3 className="font-display text-base font-semibold">Assets by Category</h3>
          <div className="mt-3 space-y-2">
            {["CAT-A", "CAT-B", "CAT-C"].map((cat) => {
              const b = breakdown[cat] || { count: 0, value_usd: 0, deployed: 0 };
              const pct = t.all ? Math.round((b.count / t.all) * 100) : 0;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => onJumpToRegister?.(`category:${cat}`)}
                  className="w-full text-left rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50/60 hover:border-[#1E5CBA]/50 transition"
                  data-testid={`cat-row-${cat}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono-alt text-xs font-bold text-slate-900">{cat}</span>
                    <span className="font-mono-alt tabular-nums text-base font-bold text-slate-900">{b.count}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] font-mono-alt text-slate-500">
                    <span>{pct}% · ${fmt(Math.round(b.value_usd || 0))}</span>
                    <span>{b.deployed || 0} deployed</span>
                  </div>
                  <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#1E5CBA]" style={{ width: `${pct}%` }} />
                  </div>
                </button>
              );
            })}
            {(t.uncategorized || 0) > 0 && (
              <button
                type="button"
                onClick={() => onJumpToRegister?.("category:UNCATEGORIZED")}
                className="w-full text-left rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 hover:bg-amber-100/60 transition"
                data-testid="cat-row-uncategorized"
              >
                <div className="flex items-baseline justify-between gap-2 text-amber-800">
                  <span className="font-mono-alt text-xs font-bold flex items-center gap-1"><Warning size={11} weight="fill" /> Uncategorized</span>
                  <span className="font-mono-alt tabular-nums text-base font-bold">{t.uncategorized}</span>
                </div>
                <div className="text-[10px] font-mono-alt text-amber-700 mt-0.5">Needs CAT-A/B/C classification</div>
              </button>
            )}
          </div>
        </div>

        {/* Calibration Compliance */}
        <button
          type="button"
          onClick={() => onJumpToRegister?.("calibration:expired")}
          className="dc-card p-5 text-left transition hover:border-[#1E5CBA]/50 hover:shadow-md cursor-pointer"
          data-testid="widget-calibration"
        >
          <h3 className="font-display text-base font-semibold">Calibration Compliance</h3>
          {cal.total_requiring === 0 ? (
            <div className="mt-4 text-sm text-slate-500" data-testid="cal-no-required">No calibration-required assets recorded.</div>
          ) : (
            <>
              <div className="mt-3 text-4xl font-display font-semibold" style={{ color: compColors }} data-testid="cal-rate">
                {s.calibration_compliance_rate}%
              </div>
              <div className="mt-1 text-xs text-slate-500">{cal.valid} / {cal.total_requiring} with valid current calibration</div>
              <div className="mt-3 grid grid-cols-2 gap-1.5 text-[11px]">
                <div className="rounded border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 text-emerald-800">
                  <div className="overline">Valid</div>
                  <div className="font-mono-alt tabular-nums font-bold">{cal.valid}</div>
                </div>
                <div className="rounded border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-amber-800">
                  <div className="overline">Due 30d</div>
                  <div className="font-mono-alt tabular-nums font-bold">{cal.due_soon_30d}</div>
                </div>
                <div className="rounded border border-red-200 bg-red-50/60 px-2 py-1.5 text-red-800">
                  <div className="overline">Overdue</div>
                  <div className="font-mono-alt tabular-nums font-bold">{cal.overdue}</div>
                </div>
                <div className="rounded border border-slate-200 bg-slate-50/60 px-2 py-1.5 text-slate-700">
                  <div className="overline">Missing</div>
                  <div className="font-mono-alt tabular-nums font-bold">{cal.missing}</div>
                </div>
              </div>
              {cal.missing > 0 && (
                <div className="mt-2 text-[11px] text-amber-700" data-testid="cal-data-incomplete">
                  <Warning size={11} weight="fill" className="inline mr-1" /> Calibration data incomplete for {cal.missing} asset(s).
                </div>
              )}
            </>
          )}
        </button>

        {/* Action Needed */}
        <div className="dc-card p-5" data-testid="widget-action-needed">
          <div className="flex items-baseline justify-between">
            <h3 className="font-display text-base font-semibold">Action Needed</h3>
            <span className={`font-mono-alt text-[10px] uppercase tracking-widest font-bold ${s.action_total > 0 ? "text-red-700" : "text-emerald-700"}`}>{s.action_total} total</span>
          </div>
          {actionList.length === 0 ? (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm text-emerald-800" data-testid="action-clean">
              <CheckCircle size={13} weight="fill" className="inline mr-1" /> No actionable items right now.
            </div>
          ) : (
            <div className="mt-3 space-y-1.5">
              {actionList.map((a) => {
                const TONE = {
                  red: "text-red-700",
                  amber: "text-amber-700",
                  blue: "text-blue-700",
                }[a.tone] || "text-slate-900";
                return (
                  <button
                    key={a.k}
                    type="button"
                    onClick={() => onJumpToRegister?.(`action:${a.k}`)}
                    className="w-full text-left flex items-center justify-between text-sm px-2 py-1.5 rounded hover:bg-slate-50/60"
                    data-testid={`action-${a.k}`}
                  >
                    <span className="text-slate-700">{a.l}</span>
                    <span className={`font-mono-alt tabular-nums font-bold ${TONE}`}>{action[a.k]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ── REGISTER ─────────────────────────────────────────────────────────
function Register({ presetFilter, onClearFilter }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [expandedAsset, setExpandedAsset] = useState(null);
  const [lifecycleAsset, setLifecycleAsset] = useState(null);
  const { newId, highlightRef, classForId } = useNewRecordHighlight(loading);

  // Honour the optional preset filter from Overview/Categories drill-through
  useEffect(() => {
    if (!presetFilter) return;
    if (presetFilter.kind === "category" && ["CAT-A", "CAT-B", "CAT-C"].includes(presetFilter.value)) {
      setCategoryFilter(presetFilter.value);
    } else {
      setCategoryFilter("all");
    }
  }, [presetFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const q = categoryFilter === "all" ? "" : `?category=${categoryFilter}`;
      const { data } = await api.get(`/assets${q}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [categoryFilter]);

  // Client-side filtering for status / calibration / action presets
  const filtered = React.useMemo(() => {
    if (!presetFilter || presetFilter.kind === "category") return items;
    const today = new Date().toISOString().slice(0, 10);
    const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    return items.filter((a) => {
      if (presetFilter.kind === "status") return a.status === presetFilter.value;
      if (presetFilter.kind === "calibration") {
        const due = a.calibration_due;
        if (presetFilter.value === "expired") return due && due < today;
        if (presetFilter.value === "due_soon") return due && due >= today && due <= in30;
        if (presetFilter.value === "missing") return (a.calibration_frequency_months || 0) > 0 && !due;
        return true;
      }
      if (presetFilter.kind === "action") {
        if (presetFilter.value === "missing_custodian") return ["deployed", "rented"].includes(a.status) && !a.assigned_to;
        if (presetFilter.value === "calibration_overdue") return a.calibration_due && a.calibration_due < today;
        if (presetFilter.value === "calibration_due_soon") return a.calibration_due && a.calibration_due >= today && a.calibration_due <= in30;
        if (presetFilter.value === "calibration_missing") return (a.calibration_frequency_months || 0) > 0 && !a.calibration_due;
        return true;
      }
      return true;
    });
  }, [items, presetFilter]);

  const filterLabel = presetFilter
    ? presetFilter.kind === "category" ? `${presetFilter.value}`
    : presetFilter.kind === "status" ? `status · ${presetFilter.value}`
    : presetFilter.kind === "calibration" ? `calibration · ${presetFilter.value}`
    : presetFilter.kind === "action" ? `action · ${presetFilter.value}`
    : null
    : null;

  return (
    <div className="space-y-4">
      {filterLabel && (
        <div className="rounded-md border border-[#1E5CBA]/30 bg-[#1E5CBA]/5 px-4 py-2 flex items-center gap-3" data-testid="register-active-filter">
          <span className="font-mono-alt text-[11px] uppercase tracking-widest text-[#1E5CBA]">Filtered by · {filterLabel}</span>
          <span className="text-xs text-slate-600">{filtered.length} of {items.length} assets</span>
          <button
            type="button"
            onClick={() => { setCategoryFilter("all"); onClearFilter?.(); }}
            className="ml-auto rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50"
            data-testid="register-clear-filter"
          >
            Clear filter
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {["all", "CAT-A", "CAT-B", "CAT-C"].map((c) => (
            <button key={c} onClick={() => { setCategoryFilter(c); onClearFilter?.(); }}
              className={`px-3 py-1.5 rounded-md text-xs font-mono-alt uppercase tracking-wider border ${
                categoryFilter === c ? "bg-[#1E5CBA] border-[#1E5CBA] text-white" : "bg-white border-slate-200 text-slate-700"
              }`}
              data-testid={`cat-filter-${c}`}
            >{c}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AiExtractDialog
            module="assets"
            onSaved={load}
            trigger={
              <Button variant="outline" data-testid="assets-ai-extract-btn">
                <Sparkle size={14} className="mr-2" weight="fill" /> AI Extract
              </Button>
            }
          />
          <AssetCreateDialog open={open} onOpenChange={setOpen} onCreated={load} />
        </div>
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : filtered.length === 0 ? (
          <Empty title="No assets match this filter" hint="Adjust the category or clear the filter to see all assets." />
        ) : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Asset ID</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Assigned / Location</th>
                  <th className="px-5 py-3">Calibration</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Connections</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((a) => (
                  <React.Fragment key={a.id}>
                  <tr
                    ref={a.id === newId ? highlightRef : null}
                    className={`text-sm cursor-pointer hover:bg-slate-50/70 transition-colors ${classForId(a.id)}`}
                    onClick={() => setLifecycleAsset(a)}
                    data-testid={`asset-row-${a.asset_id}`}
                  >
                    <td className="px-5 py-3 font-mono-alt text-[12px] font-semibold text-slate-900">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{a.asset_id}</span>
                        {a.source === "ai_extraction" && (
                          <span className="dc-ai-chip" title="Imported via AI Extractor">AI</span>
                        )}
                        {a.source_procurement_id && (
                          <span className="font-mono-alt text-[9px] rounded bg-violet-100 px-1.5 py-0.5 text-violet-700" title="Auto-created from procurement GRN">PROC</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-700"><div className="truncate max-w-xs">{a.description}</div><div className="text-xs text-slate-500">{a.serial_number}</div></td>
                    <td className="px-5 py-3"><span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-600">{a.category}</span></td>
                    <td className="px-5 py-3 text-xs text-slate-700">{a.assigned_to || "—"}{a.location ? ` · ${a.location}` : ""}</td>
                    <td className="px-5 py-3 text-xs text-slate-700">{a.calibration_due ? formatDate(a.calibration_due) : "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={a.status === "in_store" ? "active" : a.status === "deployed" ? "pending" : a.status === "lost" ? "rejected" : a.status} /></td>
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <AssetLifecycleDrawer asset={a} />
                        <AuditTrailDrawer entity="asset" entityId={a.id} label={a.asset_id} />
                        <button
                          onClick={() => setExpandedAsset((x) => x === a.id ? null : a.id)}
                          className="font-mono-alt text-[10px] uppercase tracking-widest text-[#1E5CBA] hover:underline"
                          data-testid={`asset-connections-toggle-${a.asset_id}`}
                        >
                          {expandedAsset === a.id ? "Hide" : "View"} →
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedAsset === a.id && (
                    <tr className="bg-slate-50" data-testid={`asset-connections-${a.asset_id}`}>
                      <td colSpan={7} className="px-5 py-4">
                        <RelationsPanel entityType="asset" entityId={a.id} compact />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {filtered.map((a) => (
                <li
                  key={a.id}
                  ref={a.id === newId ? highlightRef : null}
                  className={`p-4 cursor-pointer hover:bg-slate-50/70 transition-colors ${classForId(a.id)}`}
                  onClick={() => setLifecycleAsset(a)}
                  data-testid={`asset-card-${a.asset_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-alt text-sm font-semibold">{a.asset_id}</span>
                      {a.source === "ai_extraction" && (
                        <span className="dc-ai-chip" title="Imported via AI Extractor">AI</span>
                      )}
                    </div>
                    <StatusBadge status={a.status === "in_store" ? "active" : "pending"} />
                  </div>
                  <div className="mt-1 text-sm text-slate-800">{a.description}</div>
                  <div className="mt-1 text-xs text-slate-500">{a.category} · {a.location || "—"}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {lifecycleAsset && (
        <AssetLifecycleDrawer
          asset={lifecycleAsset}
          open={!!lifecycleAsset}
          onOpenChange={(o) => { if (!o) setLifecycleAsset(null); }}
        />
      )}
    </div>
  );
}

// ── ASSET SUB-CATEGORY CATALOG ───────────────────────────────────────
// Code → label. Used by Register Asset to derive ID prefix + classify.
// Keep in sync with backend SUBCAT_PREFIX dict.
const SUBCAT_OPTIONS = [
  { code: "GEN",  label: "Generator",          category: "CAT-B" },
  { code: "VEH",  label: "Vehicle",            category: "CAT-B" },
  { code: "SVY",  label: "Survey Equipment",   category: "CAT-A" },
  { code: "RTK",  label: "RTK / GNSS",         category: "CAT-A" },
  { code: "DRN",  label: "Drone / UAV",        category: "CAT-A" },
  { code: "LAB",  label: "Lab Equipment",      category: "CAT-A" },
  { code: "LAP",  label: "Laptop / Computer",  category: "CAT-C" },
  { code: "EQU",  label: "General Equipment",  category: "CAT-B" },
  { code: "TOOL", label: "Hand Tool / Power Tool", category: "CAT-C" },
  { code: "OFF",  label: "Office Furniture",   category: "CAT-C" },
];

const ASSET_STATUS_OPTIONS = [
  ["in_store",    "In Store / Available"],
  ["deployed",    "Deployed on Project"],
  ["rented",      "Rented Out"],
  ["maintenance", "Under Maintenance"],
  ["calibration", "Awaiting Calibration"],
  ["retired",     "Retired"],
  ["lost",        "Lost / Stolen"],
];

function AssetCreateDialog({ open, onOpenChange, onCreated }) {
  const initial = {
    asset_id: "", description: "", serial_number: "", model_no: "", category: "CAT-B",
    sub_category: "", purchase_date: "", purchase_cost: "", vendor: "",
    warranty_expiry: "", calibration_frequency_months: 12, calibration_due: "",
    storage_zone: "ZONE-B", assigned_to: "", location: "", status: "in_store", notes: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [autoGen, setAutoGen] = useState(true);
  const [staff, setStaff] = useState([]);
  const [projects, setProjects] = useState([]);

  // Load staff + projects ONCE for the picker fields
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/hr/staff").catch(() => ({ data: [] })),
      api.get("/projects").catch(() => ({ data: [] })),
    ]).then(([s, p]) => {
      setStaff(s.data || []);
      setProjects(p.data || []);
    });
  }, [open]);

  // Reset on close
  useEffect(() => { if (!open) { setForm(initial); setAutoGen(true); } /* eslint-disable-next-line */ }, [open]);

  // Whenever sub_category changes (and auto-gen is on), fetch the next ID
  // and derive default category + storage_zone from the sub-cat metadata.
  useEffect(() => {
    if (!form.sub_category) return;
    const meta = SUBCAT_OPTIONS.find((s) => s.code === form.sub_category);
    const derivedCategory = meta?.category || form.category;
    const derivedZone = derivedCategory === "CAT-A" ? "ZONE-A" : derivedCategory === "CAT-B" ? "ZONE-B" : "ZONE-C";
    if (autoGen) {
      api.get(`/assets/next-id?sub_category=${form.sub_category}`)
        .then(({ data }) => {
          setForm((f) => ({
            ...f,
            asset_id: data.asset_id,
            category: f.category === initial.category ? derivedCategory : f.category, // don't clobber if user changed manually
            storage_zone: f.storage_zone === initial.storage_zone ? derivedZone : f.storage_zone,
          }));
        })
        .catch(() => { /* user can still type manually */ });
    } else {
      setForm((f) => ({ ...f, category: derivedCategory, storage_zone: derivedZone }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sub_category, autoGen]);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      payload.calibration_frequency_months = +payload.calibration_frequency_months || 0;
      payload.purchase_cost = payload.purchase_cost ? Number(payload.purchase_cost) : 0;
      // Strip empties
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post("/assets", payload);
      toast.success(`Asset ${payload.asset_id} registered`);
      onOpenChange(false); setForm(initial); onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" data-testid="asset-create-btn"><Plus size={14} /> Register Asset</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="asset-create-dialog">
        <DialogHeader>
          <DialogTitle>Register Asset</DialogTitle>
          <DialogDescription>DESCON/QMS/AR-001 — Master Asset Register entry. Asset ID is generated automatically based on the equipment type.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-6 gap-3">
          {/* 1. Equipment type → drives ID + Category + Zone defaults */}
          <div className="col-span-6 md:col-span-3">
            <Label>Equipment Type *</Label>
            <Select value={form.sub_category} onValueChange={set("sub_category")}>
              <SelectTrigger data-testid="asset-subcat-select"><SelectValue placeholder="Pick equipment type…" /></SelectTrigger>
              <SelectContent>
                {SUBCAT_OPTIONS.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500 mr-2">{s.code}</span>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-6 md:col-span-3">
            <Label className="flex items-center justify-between">
              <span>Asset ID *</span>
              <button type="button" onClick={() => setAutoGen((x) => !x)}
                className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-600 hover:underline"
                data-testid="asset-id-mode-toggle">
                {autoGen ? "Switch to manual" : "Auto-generate"}
              </button>
            </Label>
            <Input
              required
              placeholder={autoGen ? "Pick equipment type first…" : "DESCON-EQU-001"}
              value={form.asset_id}
              onChange={set("asset_id")}
              disabled={autoGen}
              data-testid="asset-id-input"
              className={autoGen ? "bg-slate-50 font-mono-alt" : "font-mono-alt"}
            />
            <div className="mt-1 text-[10px] text-slate-500">
              {autoGen ? "Auto-generated from equipment type + next sequence number." : "Manually entering — must be unique."}
            </div>
          </div>

          {/* 2. Description + Serial */}
          <div className="col-span-6 md:col-span-4">
            <Label>Description *</Label>
            <Input required placeholder="e.g. DJI Matrice 400 + L2 LiDAR, or Cummins 50kVA Generator" value={form.description} onChange={set("description")} />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Serial Number</Label>
            <Input value={form.serial_number} onChange={set("serial_number")} />
          </div>

          <div className="col-span-6 md:col-span-3">
            <Label>Model</Label>
            <Input value={form.model_no} onChange={set("model_no")} />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label>Vendor / Supplier</Label>
            <Input value={form.vendor} onChange={set("vendor")} />
          </div>

          {/* 3. Risk category & storage */}
          <div className="col-span-6 md:col-span-2">
            <Label>Risk Category *</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="asset-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CAT-A">CAT-A — Restricted</SelectItem>
                <SelectItem value="CAT-B">CAT-B — Controlled</SelectItem>
                <SelectItem value="CAT-C">CAT-C — Standard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Storage Zone</Label>
            <Select value={form.storage_zone || "none"} onValueChange={(v) => setForm({ ...form, storage_zone: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="ZONE-A">ZONE-A — Secure Vault</SelectItem>
                <SelectItem value="ZONE-B">ZONE-B — Controlled</SelectItem>
                <SelectItem value="ZONE-C">ZONE-C — General</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Initial Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="asset-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSET_STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* 4. Custodian + Location */}
          <div className="col-span-6 md:col-span-3">
            <Label>Custodian / Assigned to</Label>
            <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="asset-custodian-select"><SelectValue placeholder="Select custodian…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— Unassigned —</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.email || s.full_name}>
                    {s.full_name} <span className="text-slate-400 text-[10px]">· {s.designation || s.department || "staff"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label>Location / Deployed at</Label>
            <Select value={form.location || "none"} onValueChange={(v) => setForm({ ...form, location: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="asset-location-select"><SelectValue placeholder="Select project or warehouse…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— Not deployed —</SelectItem>
                <SelectItem value="Main Warehouse">Main Warehouse · HQ</SelectItem>
                <SelectItem value="Hargeisa Office">Hargeisa Office</SelectItem>
                <SelectItem value="Garowe Office">Garowe Office</SelectItem>
                <SelectItem value="Kismayo Office">Kismayo Office</SelectItem>
                <SelectItem value="Bosaso Office">Bosaso Office</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.name}>{p.name} <span className="text-slate-400 text-[10px]">· project site</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 5. Purchase + warranty */}
          <div className="col-span-6 md:col-span-2">
            <Label>Purchase Date</Label>
            <Input type="date" value={form.purchase_date} onChange={set("purchase_date")} />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Purchase Cost (USD)</Label>
            <Input type="number" min="0" step="0.01" value={form.purchase_cost} onChange={set("purchase_cost")} />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Label>Warranty Expiry</Label>
            <Input type="date" value={form.warranty_expiry} onChange={set("warranty_expiry")} />
          </div>

          {/* 6. Calibration */}
          <div className="col-span-6 md:col-span-3">
            <Label>Calibration Frequency (months)</Label>
            <Select value={String(form.calibration_frequency_months || 0)} onValueChange={(v) => setForm({ ...form, calibration_frequency_months: +v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Not required</SelectItem>
                <SelectItem value="3">Every 3 months</SelectItem>
                <SelectItem value="6">Every 6 months</SelectItem>
                <SelectItem value="12">Every 12 months</SelectItem>
                <SelectItem value="24">Every 24 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label>Next Calibration Due</Label>
            <Input type="date" value={form.calibration_due} onChange={set("calibration_due")} />
          </div>

          <div className="col-span-6">
            <Label>Notes</Label>
            <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={2} value={form.notes} onChange={set("notes")} placeholder="Optional remarks (condition, accessories included, etc.)" />
          </div>

          <DialogFooter className="col-span-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.asset_id} className="btn-primary-blue">{busy ? "Saving…" : "Register Asset"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── CATEGORIES ───────────────────────────────────────────────────────
function Categories({ onJumpToRegister }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/assets/summary/overview").then((r) => setS(r.data)); }, []);

  const FRAMEWORK = {
    "CAT-A": {
      sensitivity: "RESTRICTED — High",
      tone: "red",
      zone: "ZONE-A · Secure Vault",
      controls: {
        risk_level: "High",
        storage: "Steel-reinforced room · CCTV 24/7 · environmental control",
        access: "MD + Senior Technical Director only",
        approval: "Director sign-off mandatory",
        calibration: "Certified annually by external authority",
        custody: "Strict — single named custodian",
        audit_frequency: "Quarterly",
      },
      examples: "UAV/Drone (DJI M400 + LiDAR), Echo Sounder / Bathymetric systems, MASW geophone arrays, GPS Receivers, Total Stations, Soil Lab Kits, Concrete Test Press",
    },
    "CAT-B": {
      sensitivity: "CONTROLLED — Medium",
      tone: "amber",
      zone: "ZONE-B · Controlled Store",
      controls: {
        risk_level: "Medium",
        storage: "Lockable controlled-access room with inventory log",
        access: "Technical staff with written authorisation",
        approval: "Department manager approval",
        calibration: "As applicable (e.g. RTK-GNSS)",
        custody: "Department-level custodian",
        audit_frequency: "Bi-annual",
      },
      examples: "RTK-GNSS sets, GPR systems, geotechnical rigs (GY-150, XY-130), generators, plotters, pickup trucks, 4WD SUVs",
    },
    "CAT-C": {
      sensitivity: "STANDARD — General",
      tone: "blue",
      zone: "ZONE-C · General Store",
      controls: {
        risk_level: "Low",
        storage: "Padlocked general area",
        access: "All DESCON field staff",
        approval: "Logged check-out only",
        calibration: "Not applicable",
        custody: "Site-level pool",
        audit_frequency: "Annual",
      },
      examples: "Auto levels, levelling staffs, accessories, laptops, workstations, office equipment, hand tools",
    },
  };

  const breakdown = s?.category_breakdown || {};
  const uncategorized = s?.totals?.uncategorized || 0;
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

  const TONE = {
    red:    "border-red-200 text-red-700",
    amber:  "border-amber-200 text-amber-700",
    blue:   "border-blue-200 text-blue-700",
  };

  return (
    <div className="space-y-4" data-testid="categories-tab">
      {uncategorized > 0 && (
        <button
          type="button"
          onClick={() => onJumpToRegister?.("category:UNCATEGORIZED")}
          className="w-full rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 text-left hover:bg-amber-100/60 transition flex items-center gap-3"
          data-testid="cat-uncategorized-banner"
        >
          <Warning size={18} weight="fill" className="text-amber-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-semibold text-amber-900">Uncategorized Assets: {uncategorized}</div>
            <div className="text-xs text-amber-800">These assets have no CAT-A/B/C classification. Click to review and classify.</div>
          </div>
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {Object.entries(FRAMEWORK).map(([cat, d]) => {
          const b = breakdown[cat] || { count: 0, value_usd: 0, deployed: 0, in_store: 0, maintenance: 0, calibration_required: 0, calibration_missing: 0, missing_custodian: 0 };
          return (
            <div key={cat} className="dc-card overflow-hidden" data-testid={`cat-card-${cat}`}>
              <div className={`px-5 py-3 border-b border-slate-100 ${TONE[d.tone] || "border-slate-200 text-slate-700"}`}>
                <div className="font-mono-alt text-[10px] uppercase tracking-widest">{d.sensitivity}</div>
                <div className="mt-0.5 flex items-baseline justify-between">
                  <span className="font-display text-2xl font-bold text-slate-900">{cat}</span>
                  <button
                    type="button"
                    onClick={() => onJumpToRegister?.(`category:${cat}`)}
                    className="font-display text-3xl font-bold tabular-nums text-slate-900 hover:text-[#1E5CBA] transition"
                    data-testid={`cat-count-${cat}`}
                  >
                    {b.count}
                  </button>
                </div>
                <div className="text-[10px] font-mono-alt text-slate-500 mt-0.5">{d.zone}</div>
              </div>

              <div className="p-5">
                {/* Live operational counts */}
                <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                  <Stat l="Deployed" v={b.deployed} tone="emerald" testid={`cat-${cat}-deployed`} />
                  <Stat l="In Store" v={b.in_store} tone="slate" testid={`cat-${cat}-instore`} />
                  <Stat l="Maint." v={b.maintenance} tone="amber" testid={`cat-${cat}-maint`} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1.5 text-[11px]">
                  <Stat l="Cal. Req'd" v={b.calibration_required} tone="blue" testid={`cat-${cat}-calreq`} />
                  <Stat l="Cal. Missing" v={b.calibration_missing} tone={b.calibration_missing > 0 ? "amber" : "slate"} testid={`cat-${cat}-calmiss`} />
                  <Stat l="No Custodian" v={b.missing_custodian} tone={b.missing_custodian > 0 ? "red" : "slate"} testid={`cat-${cat}-nocust`} />
                </div>

                <div className="mt-3 rounded-md bg-slate-50/70 px-3 py-2 text-[11px]">
                  <div className="overline mb-1">Total Value</div>
                  <div className="font-mono-alt tabular-nums text-lg font-bold text-slate-900">${fmt(Math.round(b.value_usd || 0))}</div>
                </div>

                {/* Framework controls */}
                <div className="mt-3 space-y-1.5 text-[11px]">
                  <div className="overline">Framework controls</div>
                  {Object.entries(d.controls).map(([k, v]) => (
                    <div key={k} className="flex items-start gap-2">
                      <span className="font-mono-alt uppercase tracking-wider text-slate-500 min-w-[88px]">{k.replace("_", " ")}</span>
                      <span className="text-slate-700">{v}</span>
                    </div>
                  ))}
                </div>

                {/* Examples */}
                <div className="mt-3 text-[11px]">
                  <div className="overline mb-1">Examples</div>
                  <div className="text-slate-700 italic">{d.examples}</div>
                </div>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => onJumpToRegister?.(`category:${cat}`)}
                  className="mt-4 w-full rounded-md border border-[#1E5CBA] bg-[#1E5CBA] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0f1e42] transition"
                  data-testid={`cat-open-register-${cat}`}
                >
                  Open Asset Register · {cat}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ l, v, tone, testid }) {
  const TONE = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-800",
    red: "border-red-200 bg-red-50/60 text-red-800",
    blue: "border-blue-200 bg-blue-50/60 text-blue-800",
    slate: "border-slate-200 bg-slate-50/60 text-slate-700",
  }[tone] || "border-slate-200 bg-slate-50/60 text-slate-700";
  return (
    <div className={`rounded border px-1.5 py-1 ${TONE}`} data-testid={testid}>
      <div className="overline text-[9px]">{l}</div>
      <div className="font-mono-alt tabular-nums font-bold">{v ?? 0}</div>
    </div>
  );
}

// ── MOVEMENTS ────────────────────────────────────────────────────────
function Movements() {
  return <SimpleListWithCreate
    endpoint="/assets/movements/all"
    createEndpoint="/assets/movements"
    title="Check In / Check Out Log"
    dataKey="items"
    columns={[
      ["asset_id", "Asset ID", true],
      ["movement_type", "Type"],
      ["operator", "Operator"],
      ["destination", "Destination"],
      ["created_at", "When", false, "datetime"],
    ]}
    form={[
      { k: "asset_id", label: "Asset", required: true, type: "asset_picker",
        autofill: { operator: "assigned_to", destination: "location" } },
      { k: "movement_type", label: "Type", type: "select", required: true, options: [
        ["check_out", "Check Out"], ["check_in", "Check In"],
        ["rental_out", "Rental Out"], ["rental_in", "Rental In"], ["transfer", "Transfer"],
      ]},
      { k: "operator", label: "Operator", hint: "Auto-filled from current custodian — edit if needed." },
      { k: "destination", label: "Destination", hint: "Auto-filled from current location." },
      { k: "purpose", label: "Purpose", fullWidth: true },
      { k: "expected_return", label: "Expected Return", type: "date" },
    ]}
    testId="movement"
  />;
}

// ── MOBILISATION ─────────────────────────────────────────────────────
function Mobilisation() {
  return <SimpleListWithCreate
    endpoint="/assets/mobilisations/all"
    createEndpoint="/assets/mobilisations"
    title="Equipment Mobilisation Requests (EMR)"
    columns={[
      ["destination", "Destination", true],
      ["project_manager", "PM"],
      ["deployment_date", "Deploy", false, "date"],
      ["return_date", "Return", false, "date"],
      ["status", "Status", false, "badge"],
    ]}
    form={[
      { k: "required_assets", label: "Assets to Mobilise", required: true, type: "asset_picker_multi" },
      { k: "project_manager", label: "Project Manager", required: true },
      { k: "project_ref", label: "Project Ref" },
      { k: "destination", label: "Destination", required: true },
      { k: "deployment_date", label: "Deployment Date", type: "date", required: true },
      { k: "return_date", label: "Return Date", type: "date" },
      { k: "purpose", label: "Purpose", textarea: true },
    ]}
    testId="mob"
  />;
}

// ── ACCESSORIES ──────────────────────────────────────────────────────
function Accessories() {
  return <SimpleListWithCreate
    endpoint="/assets/accessories/all"
    createEndpoint="/assets/accessories"
    title="Accessory Inventory (DESCON/QMS/AIS-001)"
    columns={[
      ["accessory_id", "Acc ID", true],
      ["description", "Description"],
      ["parent_asset_id", "Parent Asset"],
      ["quantity", "Qty"],
      ["status", "Status", false, "badge"],
    ]}
    form={[
      { k: "parent_asset_id", label: "Parent Asset", required: true, type: "asset_picker" },
      { k: "accessory_id", label: "Accessory ID", required: true, hint: "e.g. ACC-TRIPOD-001" },
      { k: "description", label: "Description", required: true, fullWidth: true },
      { k: "quantity", label: "Quantity", type: "number" },
      { k: "status", label: "Status", type: "select", options: [
        ["active", "Active / Available"], ["in_use", "In Use"], ["damaged", "Damaged"], ["retired", "Retired"],
      ]},
    ]}
    testId="acc"
  />;
}

// ── MAINTENANCE / CALIBRATION ────────────────────────────────────────
function Maintenance() {
  return <SimpleListWithCreate
    endpoint="/assets/maintenance/all"
    createEndpoint="/assets/maintenance"
    title="Calibration & Preventive Maintenance Log (DESCON/QMS/ML-001)"
    columns={[
      ["asset_id", "Asset ID", true],
      ["maintenance_type", "Type"],
      ["date_performed", "Performed", false, "date"],
      ["performed_by", "By"],
      ["next_due_date", "Next Due", false, "date"],
    ]}
    form={[
      { k: "asset_id", label: "Asset", required: true, type: "asset_picker" },
      { k: "maintenance_type", label: "Type", type: "select", required: true, options: [
        ["preventive", "Preventive"], ["corrective", "Corrective"], ["calibration", "Calibration"],
      ]},
      { k: "date_performed", label: "Date Performed", type: "date", required: true },
      { k: "performed_by", label: "Performed By" },
      { k: "cost", label: "Cost (USD)", type: "number" },
      { k: "next_due_date", label: "Next Due", type: "date" },
      { k: "certificate_ref", label: "Certificate Ref" },
      { k: "description", label: "Description / Work done", required: true, textarea: true },
    ]}
    testId="maint"
  />;
}

// ── RENTALS ──────────────────────────────────────────────────────────
function Rentals() {
  return <SimpleListWithCreate
    endpoint="/assets/rentals/all"
    createEndpoint="/assets/rentals"
    title="Equipment Rental Agreements (DESCON/QMS/ERA-001)"
    columns={[
      ["client_name", "Client", true],
      ["rental_period_start", "From", false, "date"],
      ["rental_period_end", "To", false, "date"],
      ["daily_rate", "Rate/day"],
      ["status", "Status", false, "badge"],
    ]}
    form={[
      { k: "asset_ids", label: "Assets to Rent", required: true, type: "asset_picker_multi" },
      { k: "client_name", label: "Client Name", required: true },
      { k: "client_contact", label: "Client Contact" },
      { k: "rental_period_start", label: "From", type: "date", required: true },
      { k: "rental_period_end", label: "To", type: "date", required: true },
      { k: "daily_rate", label: "Daily Rate (USD)", type: "number" },
      { k: "deposit", label: "Deposit (USD)", type: "number" },
      { k: "agreement_ref", label: "Agreement Ref" },
    ]}
    testId="rental"
  />;
}

// ── INCIDENTS ────────────────────────────────────────────────────────
function Incidents() {
  return <SimpleListWithCreate
    endpoint="/assets/incidents/all"
    createEndpoint="/assets/incidents"
    title="Asset Incident Reports (DESCON/QMS/AIR-001)"
    columns={[
      ["asset_id", "Asset ID", true],
      ["nature", "Nature"],
      ["date_discovered", "Date", false, "date"],
      ["location", "Location"],
      ["status", "Status", false, "badge"],
    ]}
    form={[
      { k: "asset_id", label: "Asset", required: true, type: "asset_picker",
        autofill: { location: "location", reported_by: "assigned_to" } },
      { k: "nature", label: "Nature", type: "select", required: true, options: [
        ["loss", "Loss"], ["theft", "Theft"], ["damage", "Damage"], ["malfunction", "Malfunction"], ["unauthorized_use", "Unauthorized use"],
      ]},
      { k: "date_discovered", label: "Date Discovered", type: "date", required: true },
      { k: "location", label: "Location" },
      { k: "reported_by", label: "Reported By" },
      { k: "police_reference", label: "Police Reference" },
      { k: "details", label: "Details", required: true, textarea: true },
    ]}
    testId="incident"
  />;
}

// ── ZONE ACCESS LOGS ─────────────────────────────────────────────────
function ZoneLogs() {
  return <SimpleListWithCreate
    endpoint="/assets/zone-logs/all"
    createEndpoint="/assets/zone-logs"
    title="Zone Access Log (DESCON/QMS/ZA-LOG)"
    columns={[
      ["zone", "Zone", true],
      ["staff_name", "Staff"],
      ["time_in", "Time In", false, "datetime"],
      ["time_out", "Time Out", false, "datetime"],
      ["purpose", "Purpose"],
    ]}
    form={[
      { k: "zone", label: "Zone", type: "select", required: true, options: [["ZONE-A","ZONE-A"],["ZONE-B","ZONE-B"],["ZONE-C","ZONE-C"]] },
      { k: "staff_name", label: "Staff Name", required: true },
      { k: "time_in", label: "Time In", type: "datetime-local", required: true },
      { k: "time_out", label: "Time Out", type: "datetime-local" },
      { k: "purpose", label: "Purpose", required: true },
      { k: "equipment_accessed", label: "Equipment Accessed" },
    ]}
    testId="zonelog"
  />;
}

// ── KPIs ─────────────────────────────────────────────────────────────
function KPIs() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/assets/summary/overview").then((r) => setS(r.data)); }, []);
  if (!s) return <Loading />;
  const kpis = [
    { name: "Calibration Compliance Rate", def: "% of assets with current, valid calibration certificates.", target: "≥ 95%", value: `${s.calibration_compliance_rate}%` },
    { name: "Open Incidents", def: "Asset Incident Reports currently open or under investigation.", target: "0", value: s.open_incidents },
    { name: "Pending Mobilisations", def: "Equipment Mobilisation Requests awaiting approval.", target: "≤ 3", value: s.pending_mobilisations },
    { name: "Assets Deployed", def: "Assets currently in the field or on rental.", target: "—", value: s.totals.deployed + s.totals.rented },
    { name: "Maintenance Backlog", def: "Assets currently in maintenance or calibration.", target: "Monitor", value: s.totals.maintenance },
  ];
  return (
    <div className="dc-card overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-5 py-3">KPI</th><th className="px-5 py-3">Definition</th><th className="px-5 py-3">Target</th><th className="px-5 py-3">Current</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {kpis.map((k) => (
            <tr key={k.name} className="text-sm">
              <td className="px-5 py-3 font-semibold text-slate-900">{k.name}</td>
              <td className="px-5 py-3 text-slate-600">{k.def}</td>
              <td className="px-5 py-3 font-mono-alt text-xs text-slate-500">{k.target}</td>
              <td className="px-5 py-3 font-display text-lg font-semibold text-slate-900">{k.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── FORMS REGISTER ───────────────────────────────────────────────────
function FormsRegister() {
  const forms = [
    ["DESCON/QMS/AR-001", "Master Asset Register", "All assets", "Permanent"],
    ["DESCON/QMS/EJN-001", "Equipment Justification Note", "Procurement — all CATs", "7 years"],
    ["DESCON/QMS/GRF-001", "Goods Receipt Form", "New asset intake", "Life of asset"],
    ["DESCON/QMS/AIS-001", "Accessory Inventory Sheet", "CAT-A & B accessories", "Life of asset"],
    ["DESCON/QMS/EMR-001", "Equipment Mobilisation Request", "All field deployments", "7 years"],
    ["DESCON/QMS/EMC-001", "Equipment Mobilisation Checklist", "All field deployments", "3 years"],
    ["DESCON/QMS/EIR-001", "Equipment Issue Record", "Operator assignment", "3 years"],
    ["DESCON/QMS/ERA-001", "Equipment Rental Agreement", "External loans / rentals", "7 years"],
    ["DESCON/QMS/AIR-001", "Asset Incident Report", "All incidents", "Permanent"],
    ["DESCON/QMS/ML-001", "Maintenance & Calibration Log", "All measuring assets", "Life of asset"],
    ["DESCON/QMS/ZA-LOG", "Zone A Access Log", "Zone A entries", "5 years"],
  ];
  return (
    <div className="dc-card overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-5 py-3">Form Ref</th><th className="px-5 py-3">Title</th><th className="px-5 py-3">Applies To</th><th className="px-5 py-3">Retention</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {forms.map(([ref, title, scope, ret]) => (
            <tr key={ref} className="text-sm">
              <td className="px-5 py-3 font-mono-alt text-xs font-semibold text-[#1E5CBA]">{ref}</td>
              <td className="px-5 py-3 text-slate-900">{title}</td>
              <td className="px-5 py-3 text-slate-600">{scope}</td>
              <td className="px-5 py-3 text-slate-600">{ret}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Generic list + create for most sub-modules ───────────────────────
function SimpleListWithCreate({ endpoint, createEndpoint, title, columns, form, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const inspector = useRecordInspector();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const renderCell = (row, col) => {
    const [k, , , fmt] = col;
    const v = row[k];
    if (v == null || v === "") return "—";
    if (fmt === "date") return formatDate(v);
    if (fmt === "datetime") return v.slice(0, 16).replace("T", " ");
    if (fmt === "badge") return <StatusBadge status={v === "in_store" || v === "active" ? "active" : v === "new" || v === "pending" ? "pending" : v === "lost" ? "rejected" : v} />;
    if (Array.isArray(v)) return v.join(", ");
    return String(v);
  };

  const openInspector = (r) => {
    const firstCol = columns[0];
    const titleVal = r[firstCol[0]] ? String(r[firstCol[0]]) : (r.asset_id || r.name || "Record details");
    inspector.open(r, { title: titleVal, subtitle: title });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-sm font-semibold text-slate-900 md:text-base">{title}</h3>
        <CreateDialog open={open} onOpenChange={setOpen} endpoint={createEndpoint} form={form} onCreated={load} testId={testId} />
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : items.length === 0 ? (
          <Empty title="No records yet" hint="Create the first entry using the button above." />
        ) : (
          <>
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>{columns.map((c) => <th key={c[0]} className="px-5 py-3">{c[1]}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r, i) => (
                  <tr
                    key={r.id || i}
                    className="text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => openInspector(r)}
                    data-testid={`${testId}-row-${i}`}
                  >
                    {columns.map((c) => (
                      <td key={c[0]} className={`px-5 py-3 ${c[2] ? "font-mono-alt text-[12px] font-semibold text-slate-900" : "text-slate-700"}`}>
                        {renderCell(r, c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <ul className="divide-y divide-slate-100 md:hidden">
              {items.map((r, i) => (
                <li
                  key={r.id || i}
                  className="p-4 text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                  onClick={() => openInspector(r)}
                  data-testid={`${testId}-card-${i}`}
                >
                  <div className="font-mono-alt text-xs font-semibold text-slate-900">{renderCell(r, columns[0])}</div>
                  {columns.slice(1).map((c) => (
                    <div key={c[0]} className="mt-1 text-xs text-slate-600"><span className="text-slate-400">{c[1]}: </span>{renderCell(r, c)}</div>
                  ))}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}

function CreateDialog({ open, onOpenChange, endpoint, form, onCreated, testId }) {
  const initial = Object.fromEntries(form.map((f) => [f.k, f.type === "asset_picker_multi" ? [] : ""]));
  const [vals, setVals] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {};
      for (const f of form) {
        let v = vals[f.k];
        if (f.type === "asset_picker_multi") {
          if (!v || v.length === 0) continue;
          payload[f.k] = v;
          continue;
        }
        if (v === "") continue;
        if (f.type === "number") v = Number(v);
        if (f.split) v = v.split(",").map((x) => x.trim()).filter(Boolean);
        payload[f.k] = v;
      }
      await api.post(endpoint, payload);
      toast.success("Created");
      onOpenChange(false); setVals(initial); onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  // When an asset is selected in an `asset_picker` field, auto-fill any
  // declared `autofill` siblings (e.g. operator ← assigned_to, destination ← location)
  const handleAssetPicked = (f, assetId, asset) => {
    const updates = { [f.k]: assetId };
    if (asset && f.autofill) {
      for (const [siblingKey, assetField] of Object.entries(f.autofill)) {
        // Only overwrite if the sibling field is currently empty (don't clobber user input)
        const current = vals[siblingKey];
        if (!current || current === "") {
          const v = asset[assetField];
          if (v) updates[siblingKey] = v;
        }
      }
    }
    setVals((prev) => ({ ...prev, ...updates }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" data-testid={`${testId}-create-btn`}><Plus size={14} /> New</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid={`${testId}-create-dialog`}>
        <DialogHeader><DialogTitle>New record</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {form.map((f) => {
            const isFullWidth = f.textarea || f.type === "asset_picker" || f.type === "asset_picker_multi" || f.fullWidth;
            return (
            <div key={f.k} className={isFullWidth ? "col-span-2" : "col-span-2 md:col-span-1"}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "asset_picker" ? (
                <AssetPicker
                  value={vals[f.k]}
                  status={f.assetStatus}
                  onChange={(id, asset) => handleAssetPicked(f, id, asset)}
                  testid={`${testId}-${f.k}-picker`}
                />
              ) : f.type === "asset_picker_multi" ? (
                <AssetPickerMulti
                  value={vals[f.k] || []}
                  onChange={(ids) => setVals({ ...vals, [f.k]: ids })}
                  testid={`${testId}-${f.k}-picker`}
                />
              ) : f.type === "select" ? (
                <Select value={vals[f.k]} onValueChange={(v) => setVals({ ...vals, [f.k]: v })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.textarea ? (
                <textarea className="w-full rounded-md border border-slate-200 p-2 text-sm" rows={3}
                          value={vals[f.k]} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} required={f.required} />
              ) : (
                <Input type={f.type || "text"} value={vals[f.k]}
                       onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} required={f.required} />
              )}
              {f.hint && <div className="mt-1 text-[10px] text-slate-500">{f.hint}</div>}
            </div>
          );})}
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy} className="btn-primary-blue">{busy ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Multi-asset picker — used by rentals where one record can cover multiple assets.
function AssetPickerMulti({ value = [], onChange, testid }) {
  const add = (id) => {
    if (!id) return;
    if (value.includes(id)) return;
    onChange?.([...value, id]);
  };
  const remove = (id) => onChange?.(value.filter((x) => x !== id));
  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {value.map((id) => (
            <span key={id} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 font-mono-alt text-[11px] text-slate-800">
              {id}
              <button type="button" onClick={() => remove(id)} className="text-rose-600 hover:text-rose-800"><X size={10} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
      <AssetPicker
        value=""
        onChange={(id) => add(id)}
        placeholder="Add another asset…"
        testid={testid}
      />
    </div>
  );
}

const Loading = () => <div className="p-8 text-center text-sm text-slate-500">Loading…</div>;
const Empty = ({ title, hint }) => (
  <div className="p-10 text-center">
    <Package size={40} className="mx-auto text-slate-300" />
    <div className="mt-3 text-sm font-semibold text-slate-700">{title}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);
